// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import FlashSalesService from "../../../../services/Web/flashSales.service"
import { toastSuccess, toastError } from "../../../../utility/toastutill"

// --------------------------------------------------
// GET PUBLIC ACTIVE FLASH SALES
// --------------------------------------------------
export const getActiveFlashSales = createAsyncThunk(
  "flashSales/getActiveFlashSales",
  async () => {
    try {
      const res = await FlashSalesService.getPublicActiveFlashSales()
      return res.data?.data || []
    } catch (error) {
      toastError(error)
      return []
    }
  }
)

// --------------------------------------------------
// GET ADMIN Flash Sales (Searchable and paginated)
// --------------------------------------------------
export const getAdminFlashSales = createAsyncThunk(
  "flashSales/getAdminFlashSales",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await FlashSalesService.getAdminFlashSales(params)
      return res.data || {}
    } catch (error) {
      toastError(error)
      return rejectWithValue(error.response?.data?.message || "Failed")
    }
  }
)

// --------------------------------------------------
// GET FLASH SALE BY ID
// --------------------------------------------------
export const getFlashSale = createAsyncThunk(
  "flashSales/getFlashSale",
  async (id) => {
    try {
      const res = await FlashSalesService.getFlashSaleById(id)
      return res.data?.data || null
    } catch (error) {
      toastError(error)
      return null
    }
  }
)

// --------------------------------------------------
// GET VENDOR Flash Sales
// --------------------------------------------------
export const getVendorFlashSales = createAsyncThunk(
  "flashSales/getVendorFlashSales",
  async (vendorId) => {
    try {
      const res = await FlashSalesService.getVendorFlashSales(vendorId)
      return res.data?.data || []
    } catch (error) {
      toastError(error)
      return []
    }
  }
)

// --------------------------------------------------
// CREATE FLASH SALE
// --------------------------------------------------
export const addFlashSale = createAsyncThunk(
  "flashSales/addFlashSale",
  async (payload, { dispatch }) => {
    try {
      const res = await FlashSalesService.createFlashSale(payload)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getVendorFlashSales(payload.userId))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

// --------------------------------------------------
// UPDATE FLASH SALE
// --------------------------------------------------
export const updateFlashSale = createAsyncThunk(
  "flashSales/updateFlashSale",
  async ({ id, payload }, { dispatch }) => {
    try {
      const res = await FlashSalesService.updateFlashSale(id, payload)
      if (res.data.success) {
        toastSuccess(res.data.message)
        await dispatch(getFlashSale(id))
      }
      return res.data.success
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

// --------------------------------------------------
// DELETE FLASH SALE
// --------------------------------------------------
export const deleteFlashSale = createAsyncThunk(
  "flashSales/deleteFlashSale",
  async (id, { dispatch, getState }) => {
    try {
      const res = await FlashSalesService.deleteFlashSale(id)
      if (res.data.success) {
        toastSuccess(res.data.message)

        const vendorId = getState()?.flashSales?.selectedVendorId
        if (vendorId) dispatch(getVendorFlashSales(vendorId))
      }
      return id
    } catch (error) {
      toastError(error)
      return null
    }
  }
)

// --------------------------------------------------
// SLICE
// --------------------------------------------------
export const flashSalesSlice = createSlice({
  name: "flashSales",
  initialState: {
    adminFlashSales: [],
    adminPagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
    vendorFlashSales: [],
    selectedFlashSale: null,
    selectedVendorId: null,
    searchText: "", // <-- Added
    loading: false,
    error: null
  },
  reducers: {
    setSelectedVendorId: (state, action) => {
      state.selectedVendorId = action.payload
    },
    setSearchText: (state, action) => {
      // <-- Added
      state.searchText = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdminFlashSales.pending, (state) => {
        state.loading = true
      })
      .addCase(getAdminFlashSales.fulfilled, (state, action) => {
        state.loading = false
        state.adminFlashSales = action.payload?.flashSales || []
        state.adminPagination = {
          page: action.payload?.pagination?.page || 1,
          limit: action.payload?.pagination?.limit || 10,
          total: action.payload?.pagination?.total || 0,
          totalPages: action.payload?.pagination?.totalPages || 0
        }
      })
      .addCase(getAdminFlashSales.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      .addCase(getVendorFlashSales.fulfilled, (state, action) => {
        state.vendorFlashSales = action.payload
      })

      .addCase(getFlashSale.fulfilled, (state, action) => {
        state.selectedFlashSale = action.payload
      })
  }
})

export const { setSelectedVendorId, setSearchText } = flashSalesSlice.actions
export default flashSalesSlice.reducer
